"use client";

import { useState, useEffect } from "react";
import {
  format,
  subMonths,
  eachDayOfInterval,
  isSameDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";
import { Card, CardContent, CardHeader } from "@/shadcnComponents/ui/card";
import { Tabs, TabsContent } from "@/shadcnComponents/ui/tabs";
import { Info, Loader2 } from "lucide-react";
import { IoMdOpen } from "react-icons/io";

// Data structure matching backend response
interface PostData {
  date: Date;
  posts: string[];
  postUrls: string[];
  metrics: {
    // views: number;
    likes: number;
    comments: number;
    reposts: number;
  };
  engagementLevel: 0 | 1 | 2 | 3 | 4;
}

interface FrequencyChartProps {
  data: PostData[];
}

export default function InfluencerFrequencyChart({
  data,
}: FrequencyChartProps) {
  const [postData, setPostData] = useState<PostData[]>([]);
  const [loading] = useState(false);
  const [error] = useState<string | null>(null);
  const [, setActiveTab] = useState("calendar");

  // Generate last 6 months of dates
  const today = new Date();
  const sixMonthsAgo = subMonths(today, 5);

  // Fetch data from API
  useEffect(() => {
    if (data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const processedData: PostData[] = data?.map((item: any) => ({
        ...item,
        date: new Date(item?.date),
      }));
      setPostData(processedData);
    }
  }, [data]);

  // Get exactly 6 months to display
  const months: Date[] = [];
  let currentDate = new Date(
    sixMonthsAgo.getFullYear(),
    sixMonthsAgo.getMonth(),
    1
  );
  // Include current month by going up to and including current month
  const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  while (currentDate <= currentMonth) {
    months.push(new Date(currentDate));
    currentDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1
    );
  }

  // Generate calendar grid for each month
  const generateMonthGrid = (month: Date) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 }); // Start week on Monday
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({
      start: calendarStart,
      end: calendarEnd,
    });

    // Group days into weeks (rows of 7)
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return weeks;
  };

  // Function to get engagement level for a specific date
  const getEngagementForDate = (date: Date): PostData | undefined => {
    return postData.find((data) => isSameDay(data.date, date));
  };

  // Function to get CSS class based on engagement level
  const getEngagementClass = (
    level: number | undefined,
    isCurrentMonth: boolean
  ) => {
    if (!isCurrentMonth) return "bg-gray-50 border border-gray-100"; // Grayed out for other month dates
    if (level === undefined || level === 0)
      return "bg-gray-100 border border-gray-200";
    if (level === 1) return "bg-red-100"; // Low engagement
    if (level === 2) return "bg-red-300"; // Below median
    if (level === 3) return "bg-red-500"; // Above median
    return "bg-red-700"; // High engagement/Viral
  };

  // Function to get engagement text
  const getEngagementText = (level: number | undefined) => {
    if (level === undefined || level === 0) return "No engagement";
    if (level === 1) return "Low engagement";
    if (level === 2) return "Below average engagement";
    if (level === 3) return "Above average engagement";
    return "High engagement/Viral";
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center h-64">
          <div className="flex items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading post frequency data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 font-medium mb-2">
              Error loading data
            </div>
            <div className="text-gray-600">{error}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Generate all month grids
  const monthGrids = months.map((month) => ({
    month,
    weeks: generateMonthGrid(month),
  }));

  // Find the maximum number of weeks across all months
  const maxWeeks = Math.max(...monthGrids.map((grid) => grid.weeks.length));

  return (
    <div className="w-full flex flex-col gap-2.5 font-inter">
      <div className="flex flex-col gap-1">
        <h2 className="text-[#4E73E2] font-[Inter] text-[14px] not-italic font-semibold leading-[20px]">
          Influencer Activity & Engagement Calendar
        </h2>
        <p className="text-[#9C9C9C] font-[Inter] text-[12px] not-italic font-medium leading-[normal]">
          Visualizing posting frequency and audience engagement over the last 6
          months
        </p>
      </div>
      <Card className="w-full bg-transparent">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <div></div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Engagement:</span>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm bg-red-100"></div>
                  <span className="text-xs">Low</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm bg-red-300"></div>
                  <span className="text-xs">Below Avg</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm bg-red-500"></div>
                  <span className="text-xs">Above Avg</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm bg-red-700"></div>
                  <span className="text-xs">High</span>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="calendar" onValueChange={setActiveTab}>
            <TabsContent value="calendar" className="mt-0">
              <div className="bg-white rounded-md p-4  border-gray-100">
                {/* Month headers */}
                <div className="grid grid-cols-6 gap-4 mb-4">
                  {months.map((month, i) => (
                    <div
                      key={i}
                      className="text-center text-sm font-medium text-gray-700"
                    >
                      {format(month, "MMM yyyy")}
                    </div>
                  ))}
                </div>

                {/* Weekday headers for each month */}
                <div className="grid grid-cols-6 gap-4 mb-2">
                  {months.map((_, monthIndex) => (
                    <div key={monthIndex} className="grid grid-cols-7 gap-1">
                      {["M", "T", "W", "T", "F", "S", "S"].map(
                        (day, dayIndex) => (
                          <div
                            key={dayIndex}
                            className="text-xs font-medium text-gray-500 text-center h-6 flex items-center justify-center"
                          >
                            {day}
                          </div>
                        )
                      )}
                    </div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="space-y-1">
                  {Array.from({ length: maxWeeks }).map((_, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-6 gap-4">
                      {monthGrids.map((monthGrid, monthIndex) => (
                        <div
                          key={monthIndex}
                          className="grid grid-cols-7 gap-1"
                        >
                          {Array.from({ length: 7 }).map((_, dayIndex) => {
                            const date = monthGrid.weeks[weekIndex]?.[dayIndex];
                            if (!date) {
                              return (
                                <div key={dayIndex} className="w-6 h-6"></div>
                              );
                            }

                            const isCurrentMonth =
                              date.getMonth() === monthGrid.month.getMonth();
                            const isInRange =
                              date >= sixMonthsAgo && date <= today;
                            const postDataForDate = getEngagementForDate(date);

                            if (!isInRange) {
                              return (
                                <div key={dayIndex} className="w-6 h-6"></div>
                              );
                            }

                            return (
                              <TooltipProvider key={dayIndex}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="relative">
                                      <div
                                        className={`w-3 h-3 rounded-sm flex items-center justify-center text-xs ${getEngagementClass(
                                          postDataForDate?.engagementLevel,
                                          isCurrentMonth
                                        )} hover:ring-2 hover:ring-blue-400 transition-all cursor-pointer ${
                                          isCurrentMonth
                                            ? "text-gray-800"
                                            : "text-gray-400"
                                        }`}
                                      >
                                        {/* {date.getDate()} */}
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  {isCurrentMonth && (
                                    <TooltipContent
                                      side="top"
                                      className="bg-white text-gray-800 border-gray-200 p-3 max-w-xs shadow-lg"
                                    >
                                      <div className="text-sm">
                                        <div className="font-bold">
                                          {format(date, "EEEE, MMMM d, yyyy")}
                                        </div>
                                        {postDataForDate ? (
                                          <>
                                            <div className="mt-2 font-medium text-blue-600">
                                              {postDataForDate.posts.length}{" "}
                                              post
                                              {postDataForDate.posts.length !==
                                              1
                                                ? "s"
                                                : ""}{" "}
                                              published
                                            </div>
                                            <div className="mt-1 text-xs text-gray-600 max-h-20 overflow-y-auto">
                                              {postDataForDate.posts.map(
                                                (post, i) => (
                                                  <div
                                                    key={i}
                                                    className="truncate flex gap-1"
                                                  >
                                                    {post}{" "}
                                                    <IoMdOpen
                                                      color="blue"
                                                      size={14}
                                                      className="cursor-pointer"
                                                      onClick={() =>
                                                        window.open(
                                                          postDataForDate
                                                            ?.postUrls[i],
                                                          "_blank"
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                )
                                              )}
                                            </div>
                                            <div className="mt-2 font-medium text-red-600 text-xs">
                                              {getEngagementText(
                                                postDataForDate.engagementLevel
                                              )}
                                            </div>
                                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                              <div className="text-center">
                                                <div className="font-bold">
                                                  {postDataForDate.metrics.likes.toLocaleString()}
                                                </div>
                                                <div>Likes</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="font-bold">
                                                  {postDataForDate.metrics.comments.toLocaleString()}
                                                </div>
                                                <div>Comments</div>
                                              </div>
                                              <div className="text-center">
                                                <div className="font-bold">
                                                  {postDataForDate.metrics.reposts.toLocaleString()}
                                                </div>
                                                <div>Reposts</div>
                                              </div>
                                            </div>
                                          </>
                                        ) : (
                                          <div className="mt-1 text-gray-500">
                                            No posts published on this date
                                          </div>
                                        )}
                                      </div>
                                    </TooltipContent>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Info className="w-4 h-4" />
                    <span>
                      Hover over cells to see post details and engagement
                      metrics
                    </span>
                  </div>
                  <div className="text-xs">
                    Total posts:{" "}
                    {postData.reduce((sum, day) => sum + day.posts.length, 0)}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
